import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ordersAPI, adminAPI } from "../../utils/api";
import {
  Search,
  Eye,
  Edit2,
  Trash2,
  Truck,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X,
  MapPin,
  User,
  Phone,
  Weight,
  DollarSign,
  Navigation,
  Star,
} from "lucide-react";
import toast from "react-hot-toast";
import InvoiceModal from "../../components/InvoiceModal";

const statusConfig = {
  pending: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Clock,
    label: "Pending",
  },
  assigned: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Truck,
    label: "Assigned",
  },
  picked_up: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    icon: Package,
    label: "Picked Up",
  },
  in_transit: {
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
    icon: Navigation,
    label: "In Transit",
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle,
    label: "Delivered",
  },
  failed: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    icon: XCircle,
    label: "Failed",
  },
  cancelled: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: XCircle,
    label: "Cancelled",
  },
};

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid Date";
  }
};

const AdminOrders = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState("");
  const [showInvoice, setShowInvoice] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: ordersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminOrders", page, search, statusFilter],
    queryFn: () =>
      ordersAPI.list({
        page,
        per_page: 10,
        search: search || undefined,
        status: statusFilter || undefined,
      }),
    keepPreviousData: true,
    retry: 2,
  });

  // FIXED: Only fetch couriers when modal is open to ensure fresh data
  const { data: couriersData, isLoading: couriersLoading } = useQuery({
    queryKey: ["availableCouriers", showAssignModal],
    queryFn: () => adminAPI.couriers({ status: "active" }),
    enabled: showAssignModal, // Only fetch when modal opens
    retry: 2,
  });

  // API returns: { success: true, data: { data: { data: [...couriers] }, stats: {...} } }
  const couriers = couriersData?.data?.data?.data?.data || [];

  const deleteMutation = useMutation({
    mutationFn: (id) => ordersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminOrders"] });
      toast.success("Order deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete order");
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, courierId }) => ordersAPI.assignCourier(id, courierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminOrders"] });
      toast.success("Courier assigned successfully");
      setShowAssignModal(false);
      setSelectedOrder(null);
      setSelectedCourier("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to assign courier");
    },
  });

  const orders = ordersData?.data?.data?.data || [];
  const pagination = ordersData?.data?.data?.meta || {};

  const handleDelete = (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this order? This action cannot be undone.",
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleAssign = () => {
    if (!selectedCourier || !selectedOrder) {
      toast.error("Please select a courier");
      return;
    }
    assignMutation.mutate({ id: selectedOrder.id, courierId: selectedCourier });
  };

  const openInvoice = (order) => {
    setSelectedOrder(order);
    setShowInvoice(true);
  };

  const openDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load orders</p>
        <button
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["adminOrders"] })
          }
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">Manage all orders</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tracking number, customer name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field md:w-48"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="picked_up">Picked Up</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Tracking #
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Customer
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Courier
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Amount
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Date
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No orders found</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const StatusIcon = statusConfig[order.status]?.icon || Clock;
                  return (
                    <tr
                      key={order.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <span className="text-sm font-mono font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">
                          {order.tracking_number}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {order.customer?.name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {order.courier?.name ? (
                          <span className="flex items-center">
                            <Truck className="w-3 h-3 mr-1 text-blue-500" />
                            {order.courier.name}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig[order.status]?.color || "bg-gray-100"}`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[order.status]?.label || order.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                        EGP {formatMoney(order.total_amount)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => openDetails(order)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openInvoice(order)}
                            className="p-2 text-gray-400 hover:text-violet-600 transition-colors rounded-lg hover:bg-violet-50"
                            title="View Invoice"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {order.status === "pending" && (
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowAssignModal(true);
                                setSelectedCourier(""); // Reset selection
                              }}
                              className="p-2 text-gray-400 hover:text-emerald-600 transition-colors rounded-lg hover:bg-emerald-50"
                              title="Assign Courier"
                            >
                              <Truck className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="p-2 text-gray-400 hover:text-rose-600 transition-colors rounded-lg hover:bg-rose-50"
                            title="Delete Order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {pagination.from || 0} to {pagination.to || 0} of{" "}
              {pagination.total || 0} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-600 font-medium">
                Page {page} of {pagination.last_page}
              </span>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.last_page, p + 1))
                }
                disabled={page === pagination.last_page}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Order Details</h3>
                  <p className="text-sm text-gray-500">
                    {selectedOrder.tracking_number}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedOrder(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${statusConfig[selectedOrder.status]?.color || "bg-gray-100"}`}
                >
                  {(() => {
                    const Icon =
                      statusConfig[selectedOrder.status]?.icon || Clock;
                    return <Icon className="w-4 h-4 mr-1.5" />;
                  })()}
                  {statusConfig[selectedOrder.status]?.label ||
                    selectedOrder.status}
                </span>
                <span className="text-sm text-gray-500">
                  {formatDate(selectedOrder.created_at)}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Pickup
                    </p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedOrder.pickup_address}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Delivery
                    </p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedOrder.delivery_address}
                  </p>
                  <div className="flex items-center mt-2 text-sm text-gray-600">
                    <User className="w-3.5 h-3.5 mr-1" />
                    {selectedOrder.recipient_name}
                    <Phone className="w-3.5 h-3.5 ml-3 mr-1" />
                    {selectedOrder.recipient_phone}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pb-2 border-b-2 border-gray-100">
                  Package Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <Package className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="font-semibold text-gray-900 capitalize">
                      {selectedOrder.package_type}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <Weight className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Weight</p>
                    <p className="font-semibold text-gray-900">
                      {selectedOrder.weight} kg
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <Truck className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Delivery</p>
                    <p className="font-semibold text-gray-900 capitalize">
                      {selectedOrder.delivery_type?.replace("_", " ")}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <DollarSign className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Payment</p>
                    <p className="font-semibold text-gray-900 capitalize">
                      {selectedOrder.payment_method}
                    </p>
                  </div>
                </div>
              </div>

              {selectedOrder.dimensions && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Dimensions</p>
                  <p className="font-semibold text-gray-900">
                    {selectedOrder.dimensions}
                  </p>
                </div>
              )}
              {selectedOrder.notes && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <p className="text-xs text-amber-700 uppercase font-medium mb-1">
                    Notes
                  </p>
                  <p className="text-sm text-amber-900">
                    {selectedOrder.notes}
                  </p>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pb-2 border-b-2 border-gray-100">
                  Cost Breakdown
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span className="font-medium">
                      EGP {formatMoney(selectedOrder.delivery_fee)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Weight Charge</span>
                    <span className="font-medium">
                      EGP {formatMoney(selectedOrder.weight * 2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">
                      EGP {formatMoney(selectedOrder.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Tax (10%)</span>
                    <span className="font-medium">
                      EGP {formatMoney(selectedOrder.tax)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 text-lg font-bold border-t-2 border-gray-200 mt-2">
                    <span>Total Amount</span>
                    <span className="text-primary-600">
                      EGP {formatMoney(selectedOrder.total_amount)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedOrder.courier && (
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedOrder.courier.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedOrder.courier.email} •{" "}
                        {selectedOrder.courier.phone}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedOrder.customer && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-violet-100 rounded-lg">
                      <User className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedOrder.customer.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedOrder.customer.email} •{" "}
                        {selectedOrder.customer.phone}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowDetails(false);
                    openInvoice(selectedOrder);
                  }}
                  className="flex-1 btn-primary flex items-center justify-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Invoice
                </button>
                {selectedOrder.status === "pending" && (
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setShowAssignModal(true);
                      setSelectedCourier("");
                    }}
                    className="flex-1 btn-secondary flex items-center justify-center"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    Assign Courier
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Courier Modal - FIXED */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Assign Courier
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Order:{" "}
              <span className="font-mono font-medium text-gray-700">
                {selectedOrder?.tracking_number}
              </span>
            </p>

            {couriersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <p className="ml-2 text-gray-500">Loading couriers...</p>
              </div>
            ) : couriers.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Truck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No active couriers available</p>
              </div>
            ) : (
              <div className="space-y-4">
                <select
                  value={selectedCourier}
                  onChange={(e) => setSelectedCourier(e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Select a courier...</option>
                  {couriers.map((courier) => (
                    <option key={courier.id} value={courier.id}>
                      {courier.name} — {courier.phone || "No phone"} (
                      {courier.orders_count || 0} active deliveries)
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedCourier("");
                  setSelectedOrder(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={
                  !selectedCourier ||
                  assignMutation.isPending ||
                  couriers.length === 0
                }
                className="btn-primary disabled:opacity-50 flex items-center"
              >
                {assignMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Assign Courier
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoice && selectedOrder && (
        <InvoiceModal
          order={selectedOrder}
          onClose={() => {
            setShowInvoice(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminOrders;
